import { useEffect, useRef } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import { mapStyleFor } from "~/features/maps/map-styles";
import type { GeoMapProps } from "~/features/maps/types";
import { fonts, palette, radii, spacing, useTheme } from "~/styles/theme";

export function GeoMap({
  style,
  center,
  zoom,
  markers,
  placing = false,
  onPressMap,
  onPressMarker,
  onRegionChange,
}: GeoMapProps) {
  const { colors } = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  // Callbacks are read through a ref so re-renders don't tear down the map.
  const handlersRef = useRef({ onPressMap, onPressMarker, onRegionChange });
  handlersRef.current = { onPressMap, onPressMarker, onRegionChange };
  const placingRef = useRef(placing);
  placingRef.current = placing;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: mapStyleFor(style) as maplibregl.StyleSpecification,
      center: [center.lng, center.lat],
      zoom,
    });
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.on("click", (event) => {
      if (!placingRef.current) return;
      handlersRef.current.onPressMap?.({
        lat: event.lngLat.lat,
        lng: event.lngLat.lng,
      });
    });
    map.on("moveend", () => {
      const nextCenter = map.getCenter();
      handlersRef.current.onRegionChange?.({
        lat: nextCenter.lat,
        lng: nextCenter.lng,
        zoom: map.getZoom(),
      });
    });
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // The map instance owns its own lifecycle; style and centre are synced below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mapRef.current?.setStyle(
      mapStyleFor(style) as maplibregl.StyleSpecification,
    );
  }, [style]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    for (const marker of markersRef.current) marker.remove();
    markersRef.current = markers.map((marker) => {
      const element = document.createElement("button");
      element.type = "button";
      element.textContent = marker.label;
      element.setAttribute("aria-label", marker.label);
      Object.assign(element.style, {
        maxWidth: "160px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        padding: `6px ${spacing.s}px`,
        border: `2px solid ${colors.background}`,
        borderRadius: `${radii.pill}px`,
        background: marker.selected ? palette.brandMarigold : colors.primary,
        color: marker.selected
          ? palette.brandMidnight
          : colors.primaryForeground,
        fontFamily: fonts.medium,
        fontSize: "11px",
        cursor: "pointer",
      });
      element.addEventListener("click", (event) => {
        event.stopPropagation();
        handlersRef.current.onPressMarker?.(marker.id);
      });
      return new maplibregl.Marker({ element, anchor: "bottom" })
        .setLngLat([marker.lng, marker.lat])
        .addTo(map);
    });
  }, [markers, colors]);

  return (
    <div
      ref={containerRef}
      style={{ flex: 1, minHeight: 320, width: "100%", height: "100%" }}
    />
  );
}
